import type { FC } from 'react';

import { Placeholder, useCms, useContent, useImageMapper, useRendering, useSetting } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import RichText from '@eneco/components/RichText/RichText';
import { Box, Card, Heading, Image, Stack, Stretch, Text, TextLink } from '@eneco/sparky';
import type { BoxProps, HeadingProps } from '@eneco/sparky/types';

import type { ContentCardRebrandedContent } from './content-card-rebranded-content.types';
import { CONTENT_CARD_SLOTS, resolveSlot } from './slots';

/**
 * `ContentCardRebranded` block for the CMS-agnostic lane, serving **both** providers. The rebranded
 * sibling of `ContentCard.tsx` — same shape, fewer fields: no ribbon, badge or card number.
 *
 * `buttonLink`/`link` carry their own display text on stored content, even though the generated
 * schema does not declare it (the dataset is schemaless).
 *
 * Reuses `CONTENT_CARD_SLOTS` rather than declaring its own: the legacy component renders the same
 * `jss-promo-box` overlay (`libs/sitecore/components/content/src/ContentCardRebranded`), so the slot
 * names are identical and duplicating the list would only let the two drift.
 */
const ContentCardRebranded: FC = () => {
  const { adapter } = useCms();
  const content = useContent<ContentCardRebrandedContent>();
  const rendering = useRendering();
  const mapImage = useImageMapper();

  const isElevated = useSetting('setElevation') !== 'false';
  const backgroundColor = useSetting('backgroundColor') as BoxProps['backgroundColor'];
  const headingLevel = (useSetting('headingLevel') || 'h3') as HeadingProps['as'];
  const objectFit = useSetting('objectFit') === 'contain' ? 'contain' : 'cover';

  const image = mapImage(content.image);
  const richText = adapter.toRichText(content.content);
  const promoSlot = resolveSlot(rendering, CONTENT_CARD_SLOTS.promo);
  const cta = linkWithUrl(content.buttonLink) ?? linkWithUrl(content.link);

  const body = (
    <Stretch>
      <Stack gap="0">
        {image?.src && (
          <Stack.Item shrink={false}>
            <Image src={image.src} alt={image.alt ?? ''} width="100%" objectFit={objectFit} aspectRatio={image.aspectRatio} />
          </Stack.Item>
        )}
        {promoSlot ? <Placeholder name={promoSlot} /> : null}
        {(content.title || richText) && (
          <Box padding={isElevated ? '6' : '0'} paddingTop={isElevated || image?.src ? '6' : '0'}>
            <Stack gap="6">
              {content.title && (
                <Heading as={headingLevel} size={{ initial: 'XS', md: 'S' }}>
                  {content.title}
                </Heading>
              )}
              {richText && (
                <Text size={{ initial: 'BodyM', md: 'BodyL' }}>
                  <RichText html={richText} />
                </Text>
              )}
              {cta?.text && (
                <Stack.Item grow={false}>
                  <TextLink href={cta.href} target={cta.target} emphasis="high">
                    {cta.text}
                  </TextLink>
                </Stack.Item>
              )}
            </Stack>
          </Box>
        )}
      </Stack>
    </Stretch>
  );

  if (!isElevated) return <Stack>{body}</Stack>;

  return (
    <Stack>
      <Stretch>
        <Card overflow="hidden">
          <Stretch>
            <Box backgroundColor={backgroundColor ?? 'backgroundPrimary'}>{body}</Box>
          </Stretch>
        </Card>
      </Stretch>
    </Stack>
  );
};

export default ContentCardRebranded;
